// Master JS for Calendar view
// Assumes existence of global variable window.CurrentTrain

import BootstrapModal from "bootstrap/js/dist/modal";
import page from "page";

import { PostJSON, PostJSONMultiLoader, PostJSONWithLoadError } from "./lib/fetchers.js";
import { SetElementsVisible, WireupHandlers } from "./lib/data-wireup.js";
import { WidthObserver } from "./lib/width-observer.js";
import { IsDateInPast } from "./lib/date-utilities.js";
import { TrackClick } from "./lib/click-tracking.js";
import { ShowParticipantsModal } from "./lib/participants-modal.js";
import { ShowLinkedUpdate } from "./lib/linked-update-modal.js";

import { TextRevealer } from "./webcomponents/text-revealer.js";
import { TrainPost } from "./webcomponents/train-post.js";
import { MealDateRow } from "./webcomponents/meal-date-row.js";
import { RecentActivityRow } from "./webcomponents/recent-activity-row.js";
import { CalendarDisplay } from "./webcomponents/calendar-display.js";
import { CalendarList } from "./webcomponents/calendar-list.js";
import { ShareOptions } from "./webcomponents/share-options.js";
import { ProgressBar } from "./webcomponents/progress-bar.js";
import { TrainInfoCard } from "./webcomponents/train-info-card.js";
import { FacePile } from "./webcomponents/face-pile.js";

// Register required webcomponents
TextRevealer.Register();
TrainPost.Register();
MealDateRow.Register();
RecentActivityRow.Register();
CalendarDisplay.Register();
CalendarList.Register();
ShareOptions.Register();
ProgressBar.Register();
TrainInfoCard.Register();
FacePile.Register();

// Activate the resizeObserver
WidthObserver.PerformWireup();

////// PARTICIPANTS MODAL

// Wire up the "View all" button
WireupHandlers("showparticipants", "click", () => ShowParticipantsModal("modalTrainParticipants"));

////// UPDATES

async function ShowRemainingUpdates() {
    const prependElement = document.getElementById("trainUpdates").querySelector("[data-prepend-target]");
    const posts = await PostJSONWithLoadError(prependElement, "/trains/" + window.CurrentTrain.Id + "/more_updates");
    const parent = prependElement.parentNode;
    for (const post of posts) {
        const postElement = parent.insertBefore(document.createElement("train-post"), prependElement);
        postElement.entity = post;
    }
}
WireupHandlers("showupdates", "click", ShowRemainingUpdates);

////// CALENDAR VIEW

// Populate calendars on page load
window.addEventListener("DOMContentLoaded", async () => {
    const listDiv = document.querySelector("[data-toggle-list]");
    const calendarDiv = document.querySelector("[data-toggle-calendar]");
    const pastModalDiv = document.querySelector("#modalPastDates [data-replacement-target]");

    if (!listDiv || !calendarDiv || !pastModalDiv) return;

    const result = await PostJSONMultiLoader([listDiv, calendarDiv], "/trains/" + window.CurrentTrain.Id + "/calendar");
    const dates = result.obj;

    const calendarDisplay = document.createElement("calendar-display");
    calendarDisplay.dates = dates;
    calendarDiv.innerHTML = "";
    calendarDiv.append(calendarDisplay);

    const calendarList = document.createElement("calendar-list");
    calendarList.dates = dates;
    listDiv.innerHTML = "";
    listDiv.append(calendarList);

    const pastList = document.createElement("calendar-list");
    pastList.dates = dates;
    pastList.expanded = true;
    pastList.past = true;
    pastModalDiv.innerHTML = "";
    pastModalDiv.append(pastList);

    const futureDates = dates.filter(item => !IsDateInPast(item.Entity.Date));
    if (futureDates.length > 0) {
        SetElementsVisible("futuredates");
    }
    const allBooked = (futureDates.length > 0 && futureDates.every(item => item.Entity.AssignedUser && item.Entity.AssignedUser.Id));
    if (allBooked) {
        SetElementsVisible("allbooked");
        document.body.classList.add("no-open-dates");
    }

    // Load last selected calendar view on load
    if (typeof (Storage) !== "undefined") {
        try {
            const selectedView = localStorage.getItem("selectedView");
            // Switch to calendar view if that's their last used view, but not if there aren't any future dates
            if (selectedView === "calendar" && futureDates.length > 0) {
                Array.from(document.querySelectorAll("[data-wireup-radiocalendar]")).map(item => { item.checked = true; });
                await ShowCalendarView(false);
            }
        } catch {
            // OK to ignore
        }
    }
});

/** Toggle between list view and calendar view*/
async function ShowCalendarView(isList) {
    const listDiv = document.querySelector("[data-toggle-list]");
    const calendarDiv = document.querySelector("[data-toggle-calendar]");
    if (typeof (Storage) !== "undefined") {
        try {
            localStorage.setItem("selectedView", isList ? "list" : "calendar");
        } catch {
            // OK to ignore
        }
    }
    if (isList) {
        listDiv.classList.remove("hidden");
        calendarDiv.classList.add("hidden");
    } else {
        calendarDiv.classList.remove("hidden");
        listDiv.classList.add("hidden");
    }
}
WireupHandlers("radiocalendar", "click", () => ShowCalendarView(false));
WireupHandlers("radiolist", "click", () => ShowCalendarView(true));

////// RECENT ACTIVITY

// Populate from JSON on page load
window.addEventListener("DOMContentLoaded", async () => {
    // This is a little weird because there are now two recent activity widgets, one for desktop and one for mobile
    const widgetElements = Array.from(document.querySelectorAll("[data-populate-recentactivity]"));
    if (widgetElements.length === 0) return;
    const replaceElements = widgetElements.map(e => e.querySelector("[data-replacement-target]"));
    const activityPage = await PostJSONMultiLoader(replaceElements, "/trains/" + window.CurrentTrain.Id + "/activities");
    if (activityPage.rows.length === 0) {
        // No activity on this train yet, hide activity
        Array.from(document.querySelectorAll(".train-activity")).map(e => e.classList.add("hidden"));
    } else {
        for (const replaceElement of replaceElements) {
            // Have to build an entirely new array of nodes for each element, since we can't reuse
            const nodes = [];
            for (const activity of activityPage.rows) {
                const node = document.createElement("recent-activity-row");
                node.activity = activity;
                nodes.push(node);
            }
            replaceElement.innerHTML = "";
            replaceElement.append(...nodes);
        }
    }
});

let fetchedActivityPages = 0;
async function ShowActivityModal() {
    const modalElement = document.getElementById("modalTrainActivity");
    const modal = BootstrapModal.getOrCreateInstance(modalElement);
    modal.show();
    // If it's the first load, fetch some activities
    if (fetchedActivityPages === 0) {
        await LoadMoreActivities();
    }
}

async function LoadMoreActivities() {
    const modalElement = document.getElementById("modalTrainActivity");
    const buttonElement = modalElement.querySelector("[data-wireup-showmoreactivity]");
    buttonElement.setAttribute("disabled", "disabled");
    let totalPages = 999;
    try {
        const prependElement = modalElement.querySelector("[data-prepend-target]");
        const activityPage = await PostJSONWithLoadError(prependElement, "/trains/" + window.CurrentTrain.Id + "/activities", { pageSize: 50, pageNumber: fetchedActivityPages + 1 });
        totalPages = Math.ceil(activityPage.total / 50);
        fetchedActivityPages += 1;
        const parent = prependElement.parentNode;
        for (const activity of activityPage.rows) {
            const node = parent.insertBefore(document.createElement("recent-activity-row"), prependElement);
            node.activity = activity;
            node.isInModal = true;
        }
    } finally {
        // Reenable the button, or remove it if we're done loading
        if (fetchedActivityPages >= totalPages) {
            buttonElement.remove();
        } else {
            buttonElement.removeAttribute("disabled");
        }
    }
}

// Wire up the "View all" button
WireupHandlers("showactivity", "click", ShowActivityModal);
WireupHandlers("showmoreactivity", "click", LoadMoreActivities);

////// SUPPORT FOR: /trains/{id}/share url

async function ShowShareModal() {
    const modalElement = document.getElementById("modalTrainShare");
    const modal = BootstrapModal.getOrCreateInstance(modalElement);
    modal.show();

    // Let other code know the user has already been shown a modal
    window.ShownAModal = true;
}

////// SUPPORT FOR: /trains/{id}/updates url

function ShowUpdatesSection() {
    const updatesElement = document.getElementById("trainUpdates");
    updatesElement.scrollIntoView();
    page("/");
}

// Show a modal to prompt for donations on load in some circumstances
async function ShowFirstDonationModal() {
    // If the user has already been shown a modal, or their browser doesn't support LocalStorage, abort
    if ((typeof window.ShownAModal !== "undefined" && window.ShownAModal) || typeof Storage === "undefined") return;
    try {
        const shownFirstDonation = localStorage.getItem("shownFirstDonation");
        // If we've never tried to show them this modal before, try now
        if (shownFirstDonation === null) {
            setTimeout(function () {
                const modalDiv = document.getElementById("modal-first-donation");
                // Modal may be missing if for example donations are turned off or you're an admin, so ignore
                if (modalDiv) {
                    localStorage.setItem("shownFirstDonation", "true");
                    const modal = BootstrapModal.getOrCreateInstance(modalDiv);
                    modal.show();
                }
            }, 1500);
        }
    } catch {
        // OK to ignore
    }
}

////// CLIENT SIDE ROUTING

// Needed because updates/:updateid encompasses updates/new
function PassthroughRoute(ctx) {
    // Needed for chrome, to prevent duplicate history entries
    page.stop();
    ctx.handled = false;
    // Redirect to the path
    window.location.href = ctx.canonicalPath;
}

page.base(window.CurrentTrain.OpenUrl);
page('/share', ShowShareModal);
page('/updates', ShowUpdatesSection);
page('/updates/new', PassthroughRoute);
page('/updates/:updateid', (ctx) => ShowLinkedUpdate(ctx.params.updateid));
page('/', ShowFirstDonationModal);
page({ hashbang: false });
const modalShare = document.getElementById("modalTrainShare");
if (modalShare) modalShare.addEventListener("hide.bs.modal", () => { page("/"); });

////// LOCAL STORAGE OF RECENT EVENTS

async function SaveToRecentList() {
    if (typeof (Storage) !== "undefined") {
        try {
            const result = await PostJSON("/trains/recent/", { source: localStorage.getItem("recent"), append: window.CurrentTrain.Id });
            if (result.ok) {
                localStorage.setItem("recent", result.obj);
            }
        } catch {
            // OK to ignore
        }
    }
}
window.addEventListener("DOMContentLoaded", async () => {
    SaveToRecentList();
});

////// CLICK TRACKING
WireupHandlers("click-tracking", "click", e => TrackClick(e.currentTarget));